import { useAppDispatch, useAppSelector } from "../../app";
import type { AppDispatch } from "../../app/store";
import { DefaultOptionType } from "antd/es/select";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchClassifierOptionsSearch } from "../utils/classifier";
import {
  changeActiveButton,
  changeActiveEntity,
  changeAnnotating,
  changeEditing,
  changeHasBeenEdited,
} from "../../app/store/appSlice";
import {
  CategorizationWithConfig,
  CategorizationAnnotation,
} from "../../configMap/interfaces/category";
import { useUpsertDerivedRecordsMutation } from "../store/metadataApi";
import selectCategorizationAnnotationId from "../selectors/selectCategorizationAnnotationId";
import {
  removeCategorizationAnnotation,
  removeIncompleteCategorization,
  updateCategorizationAnnotation,
  addLoadingCategorizationIds,
  removeLoadingCategorizationIds,
  setDerivedCategorizationOptions,
} from "../store/metadataSlice";
import useEntityAnnotations from "./useEntityAnnotations";
import { buildSearchQuery } from "../../common/utilities/requests";
import { Status } from "../../common/status/status";

import { useTranslation } from "react-i18next";
import useParentCategorization from "./useParentCategorization";
import { debounce } from "lodash";
import { findOriginalIndex } from "../../documentTypes/utils/synchronizeAnnotationHelper";
import useCategorizationEvents from "./useCategorizationEvents";
import useParentEntity from "./useParentEntity";
import { notification } from "antd";
import { calculateHideDelay } from "../../common/utilities/text";
import { EventTriggerType } from "../interfaces/categorizationEvents";
import {
  createDerivedOptionsFromResponse,
  hasDerivedExternalId,
  prepareDerivedDataForUpsert,
} from "../utils/derivedCategorization";
import { DerivedOption } from "../interfaces/classifier";

const DEFAULT_MIN_LENGTH = 3;

const useDerivedCategorization = (
  categorizationWithConfig: CategorizationWithConfig,
  index: number,
  groupBlockId?: string,
) => {
  const { categorization } = categorizationWithConfig;

  const {
    i18n: { language },
  } = useTranslation();

  const { required } = categorizationWithConfig;
  const { id, search } = categorization;

  const dispatch = useAppDispatch();

  const document = useAppSelector(
    (state) => state.documentReducer.activeDocument,
  );
  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );
  const { user } = useAppSelector((state) => state.userReducer);

  const annotations = useAppSelector(
    (state) => state.annotationReducer.tableControlAnnotations,
  );

  const categoryAnnotation = useAppSelector((state) =>
    selectCategorizationAnnotationId(state, {
      documentId: document?.id,
      categorizationId: id,
      groupBlockId: groupBlockId,
      index: index,
    }),
  );

  const {
    derivedCategorizationOptions,
    loadingCategorizationIds,
    incompleteCategorizations,
  } = useAppSelector((state) => state.metadataReducer);

  const normalizeEntityAnnotations = useEntityAnnotations();

  const {
    isCategorizationDisabledByParent,
    disabledParentTooltip,
    getAttributeFilter,
  } = useParentCategorization(
    categoryAnnotation,
    categorization,
    document?.id,
    groupBlockId,
    index,
    activeDocumentSet?.input?.attribute,
  );

  const { attributeFilter: entityParentAttributeFilter } =
    useParentEntity(categorization);

  const [emptySearchResult, setEmptySearchResult] = useState(false);
  const [loading, setLoading] = useState(false);

  const [upsertDerivedRecords] = useUpsertDerivedRecordsMutation();

  const isCategorizationLoading = useMemo(() => {
    return loadingCategorizationIds.includes(categorization.id);
  }, [loadingCategorizationIds, categorization.id]);

  const categorizationOptions = useMemo(() => {
    return derivedCategorizationOptions[categorization.id] ?? [];
  }, [derivedCategorizationOptions, categorization.id]);

  const tabId = useMemo(() => {
    if (groupBlockId) {
      return `categorization-${id}-${groupBlockId}-${index}`;
    }

    return `categorization-${id}-${index}`;
  }, [id, index, groupBlockId]);

  const isLoadingValue = useMemo(() => {
    return categoryAnnotation?.isLoading ?? false;
  }, [categoryAnnotation]);

  const selectedCategory = useMemo(() => {
    if (!categoryAnnotation) {
      return null;
    }

    if (isLoadingValue) {
      return null;
    }

    return {
      value: categoryAnnotation.value,
      visibleValue: categoryAnnotation.visibleValue,
    };
  }, [categoryAnnotation, isLoadingValue]);

  const { callEvents } = useCategorizationEvents();

  const removeIncompleteCategorizationInRedux = useCallback(
    (categorizationId: string, index: number, groupBlockId?: string) => {
      dispatch(
        removeIncompleteCategorization({
          categorizationId,
          groupBlockId,
          index,
        }),
      );
    },
    [dispatch],
  );

  const updateCategorizationAnnotationAsync =
    (payload: CategorizationAnnotation) => (dispatch: AppDispatch) => {
      return new Promise<void>((resolve) => {
        dispatch(updateCategorizationAnnotation(payload));
        resolve();
      });
    };

  const handleChange = useCallback(
    async (key: string, callback: () => void) => {
      if (!document?.id) {
        return;
      }

      let found: DerivedOption | undefined = categorizationOptions.find(
        (o) => o.key === key,
      );

      // Close dropdown immediately
      callback();

      if (found) {
        if (found.derivedData && !hasDerivedExternalId(found)) {
          dispatch(addLoadingCategorizationIds([categorization.id]));
          try {
            const dataToUpsert = prepareDerivedDataForUpsert(found.derivedData);
            const upsertRequest = {
              organizationId: user?.organization?.id || "",
              data: [dataToUpsert],
            };
            const results = await upsertDerivedRecords(upsertRequest).unwrap();

            if (results && results.length > 0) {
              const createdOption = results[0]; // Now returns OptionType format
              found = {
                ...found,
                key: createdOption.key,
                value: createdOption.value,
                label: createdOption.label,
                visibleValue: createdOption.visibleValue,
              };
            }
          } catch (error: unknown) {
            console.error("Failed to create derived record:", error);
            const errorMessage =
              "Something went wrong when saving categorization value (saving to external data)";
            notification.error({
              message: "Categorization Save Failed",
              description: errorMessage,
              duration: calculateHideDelay(errorMessage),
            });
            // Remove any partial annotation since we don't have valid external data for the output service
            dispatch(
              removeCategorizationAnnotation({
                documentId: document.id,
                categorizationId: id,
                groupBlockId,
                index,
              }),
            );
            dispatch(removeLoadingCategorizationIds([categorization.id]));
            return;
          } finally {
            dispatch(removeLoadingCategorizationIds([categorization.id]));
          }
        }
      }

      dispatch(changeHasBeenEdited(true));
      if (found) {
        const originalIndex = findOriginalIndex(
          annotations,
          [],
          groupBlockId,
          index,
        );

        await dispatch(
          updateCategorizationAnnotationAsync({
            documentId: document.id,
            categorizationId: id,
            externalDataId: found.key,
            groupBlockId,
            index,
            originalIndex,
            isByUser: true,
            visibleValue: found.visibleValue,
            value: found.value,
          }),
        );

        removeIncompleteCategorizationInRedux(id, index, groupBlockId);

        // Re-normalization is currently only relevant for fixed categorizations, not derived ones.
        normalizeEntityAnnotations(
          id,
          found.value,
          index,
          !!groupBlockId,
        ).catch(() => undefined);

        return;
      }

      dispatch(
        removeCategorizationAnnotation({
          documentId: document.id,
          categorizationId: id,
          groupBlockId,
          index,
        }),
      );
    },
    [
      document?.id,
      id,
      groupBlockId,
      index,
      dispatch,
      normalizeEntityAnnotations,
      removeIncompleteCategorizationInRedux,
      annotations,
      categorizationOptions,
      categorization,
      upsertDerivedRecords,
      user?.organization?.id,
    ],
  );

  const handleFocus = useCallback(() => {
    dispatch(changeEditing(tabId));
    dispatch(changeActiveEntity(undefined));
    dispatch(changeAnnotating(false));
    dispatch(changeActiveButton({ id: tabId }));
  }, [dispatch, tabId]);

  const handleBlur = useCallback(() => {
    dispatch(changeEditing(null));
    dispatch(changeActiveButton(undefined));
  }, [dispatch]);

  const handleSearch = useCallback(
    // Deps are deliberately narrower than the compiler can infer: it widens
    // `activeDocumentSet?.input` / `user?.organization?.id` to the whole
    // `activeDocumentSet` / `user` object. Widening would re-create this
    // callback (and the debounced wrapper built from it) whenever those Redux
    // objects are re-created with unchanged contents, causing redundant
    // requests. The narrow deps are the correct ones.
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- narrow optional-chain deps are intentional; widening causes redundant fetches
    async (value: string) => {
      if (!search || !search.endpoint) {
        return;
      }

      const minLength =
        search.minSearchLength === undefined
          ? DEFAULT_MIN_LENGTH
          : search.minSearchLength;

      if (value.length < minLength) {
        return;
      }

      try {
        setLoading(true);
        const input_ids = activeDocumentSet?.input
          ? [activeDocumentSet.input.id]
          : undefined;
        const attributeFilter = getAttributeFilter(value);

        const searchQuery = buildSearchQuery(
          search,
          value,
          null,
          language,
          input_ids,
          attributeFilter || entityParentAttributeFilter,
          user?.organization?.id ? [user.organization.id] : undefined,
        );

        const { data } = await fetchClassifierOptionsSearch(
          search.endpoint,
          searchQuery,
        );

        const derivedOptions = createDerivedOptionsFromResponse(
          data,
          categorization.id,
        );

        dispatch(
          setDerivedCategorizationOptions({
            categorizationId: categorization.id,
            options: derivedOptions,
          }),
        );

        if (data.length === 0) {
          setEmptySearchResult(true);
        } else {
          setEmptySearchResult(false);
        }
      } catch (error: unknown) {
        console.error("Failed to search derived data:", error);
        const errorMessage =
          "Something went wrong when searching for categorization data.";
        notification.error({
          message: "Failed Search",
          description: errorMessage,
          duration: calculateHideDelay(errorMessage),
        });
        setEmptySearchResult(true);
      } finally {
        setLoading(false);
      }
    },
    [
      search,
      categorization,
      language,
      getAttributeFilter,
      entityParentAttributeFilter,
      activeDocumentSet?.input,
      user?.organization?.id,
      dispatch,
    ],
  );

  const debouncedHandleSearch = useMemo(
    () => debounce(handleSearch, 500),
    [handleSearch],
  );

  const handleFilter = useCallback(
    (input: string, option?: DefaultOptionType) => {
      if (categorization.search?.endpoint) {
        return true;
      }
      // antd types `option` as optional and its `label` as the wider
      // `ReactNode`. The Select always renders its options from `OptionType`,
      // whose `label` is a string.
      const label = option!.label as string;
      return label.toLowerCase().includes(input.toLowerCase());
    },
    [categorization.search?.endpoint],
  );

  const status = useMemo((): "" | "warning" | "error" => {
    if (
      activeDocumentSet?.status === Status.ExportFailed &&
      !selectedCategory?.value &&
      required
    ) {
      return "error";
    }
    return "";
  }, [activeDocumentSet?.status, selectedCategory, required]);

  useEffect(() => {
    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [debouncedHandleSearch]);

  const isIncomplete = useMemo(() => {
    const found = incompleteCategorizations.find((bc) => {
      return (
        bc.categorizationId === categorization.id &&
        bc.index === index &&
        bc.groupBlockId === groupBlockId
      );
    });

    return !!found;
  }, [incompleteCategorizations, categorization, index, groupBlockId]);

  const hasOnClickEvents = useMemo(() => {
    return (
      categorization.events?.some(
        (event) => event.triggerType === EventTriggerType.ON_CLICK,
      ) || false
    );
  }, [categorization.events]);

  return {
    tabId,
    selectedCategory,
    categorizationOptions,
    handleChange,
    handleFocus,
    handleBlur,
    handleSearch: debouncedHandleSearch,
    handleFilter,
    status,
    isCategorizationDisabledByParent,
    disabledParentTooltip,
    isIncomplete,
    callEvents,
    isCategorizationLoading,
    emptySearchResult,
    loading,
    isLoadingValue,
    hasOnClickEvents,
  };
};

export default useDerivedCategorization;
